import { useState } from 'react';
import { useIntl } from 'react-intl';
import { v4 } from 'uuid';

import { Filters_DEPRECATED } from '~/components/Shared/Filters_DEPRECATED/Filters_DEPRECATED';
import * as Filters from '~/components/Shared/Filters_DEPRECATED/types';
import { Field as FieldType } from '~/evifields/src/types/Field';
import { FieldId } from '~/evifields/src/types/FieldId';
import { Filter } from '~/evifields/src/types/Filter';
import { getDepartmentsFilterRendererAndSerializer } from '~/features/filters/AsyncDepartmentFilterRendererSerializer';
import { getRolesFilterRendererAndSerializer } from '~/features/filters/AsyncRolesFilterRendererSerializer';
import { UserAttributes } from '~/redux/api/methods/roles/roles';
import { PilotId } from '~/types/PilotId';

import { UsersFiltersType } from './types';

interface Props {
  filters: UsersFiltersType;
  onChangeFilter: (filters: UsersFiltersType) => void;
  clientId: PilotId;
}
const getInitialFilterValues = (filters: UsersFiltersType): Filter[] => [
  {
    id: v4(),
    fieldId: 'departments',
    operatorId: filters.departments?.length ? 'contains_any' : null,
    values: filters.departments ?? [],
  },
  {
    id: v4(),
    fieldId: 'roles',
    operatorId: filters.roles?.length ? 'contains_any' : null,
    values: filters.roles ?? [],
  },
  {
    id: v4(),
    fieldId: 'status',
    operatorId: filters.status?.length ? 'contains_any' : null,
    values: filters.status ?? [],
  },
];

export const UsersFilters = ({ clientId, filters, onChangeFilter }: Props) => {
  const intl = useIntl();
  const [filterData, setFilterData] = useState<Filter[]>(
    getInitialFilterValues(filters),
  );
  const { render, serialize } = getDepartmentsFilterRendererAndSerializer({
    clientId,
  });
  const { render: roleRenderer, serialize: roleSerializer } =
    getRolesFilterRendererAndSerializer({
      clientId,
    });
  const statusesFilterOptions: {
    label: string;
    value: UserAttributes['status'];
  }[] = [
    {
      value: 'Active',
      label: intl.formatMessage({
        id: 'UsersFilters.activeStatusLabel',
        defaultMessage: 'Active',
        description: 'Label for active user status filter option',
      }),
    },
    {
      value: 'Pending',
      label: intl.formatMessage({
        id: 'UsersFilters.pendingStatusLabel',
        defaultMessage: 'Pending',
        description: 'Label for pending user status filter option',
      }),
    },
    {
      value: 'Deactivated',
      label: intl.formatMessage({
        id: 'UsersFilters.deactivatedStatusLabel',
        defaultMessage: 'Deactivated',
        description: 'Label for deactivated user status filter option',
      }),
    },
  ];

  const DEFAULT_FIELDS: Record<FieldId, Filters.Field> = {
    departments: {
      id: 'departments',
      label: intl.formatMessage({
        id: 'UsersFilters.departmentsLabel',
        defaultMessage: 'Departments',
        description: 'Label for departments filter field',
      }),
      type: 'enum_set',
      whitelistOperatorIds: ['contains_any'],
      settings: {
        __testEnableSelectAll: () => false,
        options: [],
      },
      render: render,
      serialize: serialize,
    },
    roles: {
      id: 'roles',
      label: intl.formatMessage({
        id: 'UsersFilters.rolesLabel',
        defaultMessage: 'Roles',
        description: 'Label for roles filter field',
      }),
      type: 'enum_set',
      whitelistOperatorIds: ['contains_any'],
      settings: {
        __testEnableSelectAll: () => false,
        options: [],
      },
      render: roleRenderer,
      serialize: roleSerializer,
    },
    status: {
      id: 'status',
      label: intl.formatMessage({
        id: 'UsersFilters.statusLabel',
        defaultMessage: 'Status',
        description: 'Label for status filter field',
      }),
      type: 'enum_set',
      whitelistOperatorIds: ['contains_any'],
      settings: {
        __testEnableSelectAll: () => false,
        options: statusesFilterOptions,
      },
    },
  };

  const onFilterChange = (filters: Filter[]) => {
    setFilterData(filters);
    const filtersPayload = filters.reduce((acc, filter) => {
      return {
        ...acc,
        [filter.fieldId]: filter.values,
      };
    }, {} as UsersFiltersType);
    onChangeFilter(filtersPayload);
  };

  return (
    <Filters_DEPRECATED
      fields={DEFAULT_FIELDS as Record<FieldId, FieldType>}
      filters={filterData}
      groups={[]}
      options={{
        disableManageFilters: true,
        disableRemoveFilter: true,
        disableClearFilters: true,
      }}
      onChange={onFilterChange}
    />
  );
};
